export const SITE_URL = 'https://jsonbuddy.online';

export const SEO_DATA = {
  home: {
    path: '/',
    title: 'DevTools — Free Online Developer Tools: JSON Formatter, Diff, JWT, Base64',
    description:
      'Free online developer tools that run 100% in your browser. JSON formatter & validator, JSON diff, YAML formatter, Markdown preview, text diff, JWT decoder, Base64 encoder and epoch converter. No signup, no data upload.',
  },
  'json-formatter': {
    path: '/json-formatter',
    name: 'JSON Formatter & Validator',
    title: 'JSON Formatter & Validator — Free Online JSON Beautifier | DevTools',
    description:
      'Format, validate, beautify and minify JSON online for free. Instant error detection, tree view, escape/unescape. 100% client-side — your JSON never leaves your browser.',
    intro:
      'Paste raw or minified JSON and instantly format it with proper indentation, validate its syntax with precise error messages, or minify it for production. Explore large payloads in a collapsible tree view, and escape or unescape JSON strings — all processed locally in your browser.',
    features: [
      'Beautify JSON with 2-space indentation',
      'Validate JSON and pinpoint syntax errors',
      'Minify JSON for compact payloads',
      'Interactive tree view with expand/collapse',
      'Escape and unescape JSON strings',
    ],
    faqs: [
      {
        q: 'Is my JSON data uploaded to a server?',
        a: 'No. Formatting, validation and minification all run entirely in your browser using JavaScript. Your data never leaves your machine, making it safe for sensitive payloads.',
      },
      {
        q: 'How do I fix "Unexpected token" errors in JSON?',
        a: 'This usually means a trailing comma, single quotes instead of double quotes, or unquoted keys. Paste your JSON and click Validate — the error message shows exactly what and where the problem is.',
      },
      {
        q: 'What is the difference between formatting and minifying JSON?',
        a: 'Formatting (beautifying) adds indentation and line breaks for readability. Minifying removes all whitespace to produce the smallest possible payload for APIs and storage.',
      },
      {
        q: 'Can I view large JSON files as a tree?',
        a: 'Yes. After formatting, switch the output to Tree view to explore nested objects and arrays with expand-all and collapse-all controls.',
      },
    ],
  },
  'json-diff': {
    path: '/json-diff',
    name: 'JSON Diff',
    title: 'JSON Diff — Compare Two JSON Objects Online Free | DevTools',
    description:
      'Compare two JSON objects online and highlight added, removed and changed values. Free JSON compare tool, 100% in-browser — no data upload.',
    intro:
      'Paste two JSON documents and compare them side by side. The diff highlights added, removed and modified keys and values so you can spot configuration drift, API response changes or payload regressions in seconds.',
    features: [
      'Semantic comparison of JSON objects',
      'Highlights added, removed and changed values',
      'Side-by-side view with color coding',
      'Works with deeply nested structures',
    ],
    faqs: [
      {
        q: 'Does JSON diff ignore key order?',
        a: 'Yes. JSON objects are compared semantically, so {"a":1,"b":2} and {"b":2,"a":1} are treated as identical.',
      },
      {
        q: 'Is my data safe when comparing JSON?',
        a: 'Yes. The comparison runs entirely in your browser — nothing is transmitted to any server.',
      },
      {
        q: 'Can I compare API responses with this tool?',
        a: 'Absolutely. Paste the old response on the left and the new one on the right to instantly see what a deployment or version change altered.',
      },
    ],
  },
  'yaml-formatter': {
    path: '/yaml-formatter',
    name: 'YAML Formatter & Validator',
    title: 'YAML Formatter & Validator — Free Online YAML Beautifier | DevTools',
    description:
      'Format, validate and visualize YAML online for free. Tree view for Kubernetes manifests, CI configs and docker-compose files. 100% client-side.',
    intro:
      'Clean up and validate YAML configuration files — Kubernetes manifests, GitHub Actions workflows, docker-compose files and more. Get precise syntax errors, consistent indentation, and a collapsible tree view of your document structure.',
    features: [
      'Format YAML with consistent indentation',
      'Validate syntax with clear error messages',
      'Tree view for nested structures',
      'Handles multi-document YAML',
    ],
    faqs: [
      {
        q: 'Why does my YAML fail with an indentation error?',
        a: 'YAML is whitespace-sensitive and forbids tabs. Use spaces only and keep sibling keys at the same indent level. Paste your file here and Validate to see the exact line at fault.',
      },
      {
        q: 'Can I validate Kubernetes manifests?',
        a: 'Yes — this tool validates YAML syntax for any file including Kubernetes manifests, Helm values and CI pipelines. (It checks syntax, not Kubernetes schema.)',
      },
      {
        q: 'Is my YAML uploaded anywhere?',
        a: 'No. Parsing and formatting happen locally in your browser.',
      },
    ],
  },
  'markdown-formatter': {
    path: '/markdown-formatter',
    name: 'Markdown Formatter & Previewer',
    title: 'Markdown Preview & Formatter Online — Live MD Editor | DevTools',
    description:
      'Preview Markdown live and beautify messy MD files online for free. GitHub-style rendering with tables, code blocks and task lists. 100% in-browser.',
    intro:
      'Write or paste Markdown and see a live, GitHub-style rendered preview as you type — headings, tables, code blocks, links and lists. Click Beautify to normalize inconsistent spacing, heading styles and list markers into clean, standard Markdown.',
    features: [
      'Live HTML preview while typing',
      'Beautify and normalize Markdown source',
      'Supports tables, code blocks and blockquotes',
      'Sanitized rendering (XSS-safe)',
    ],
    faqs: [
      {
        q: 'What does the Beautify button do?',
        a: 'It reformats your Markdown source with consistent heading spacing, list markers and line breaks using Prettier, without changing the rendered output.',
      },
      {
        q: 'Does the preview match GitHub rendering?',
        a: 'The preview supports standard Markdown plus common extensions like tables and fenced code blocks, closely matching what GitHub renders.',
      },
      {
        q: 'Is the rendered HTML safe?',
        a: 'Yes. The preview is sanitized with DOMPurify, so scripts or dangerous HTML embedded in Markdown cannot execute.',
      },
    ],
  },
  'text-diff': {
    path: '/text-diff',
    name: 'Text Diff Checker',
    title: 'Text Diff — Compare Two Texts Online with Highlighting | DevTools',
    description:
      'Compare two texts or code snippets online and highlight added, removed and modified lines. Git-style side-by-side and unified diff views. Free and private.',
    intro:
      'Paste two versions of any text — code, configs, contracts, articles — and get a professional git-style diff. Choose side-by-side or unified view, with additions in green, removals in red and modifications in orange, plus per-file statistics.',
    features: [
      'Git-style line-by-line comparison',
      'Side-by-side and unified diff views',
      'Added/removed/modified statistics',
      'Full-screen diff mode',
    ],
    faqs: [
      {
        q: 'What is the difference between unified and side-by-side diff?',
        a: 'Side-by-side shows both versions in two columns for visual scanning. Unified interleaves - and + lines in one column, like git diff output.',
      },
      {
        q: 'Can I diff source code with this tool?',
        a: 'Yes — it works with any plain text including source code, JSON, YAML, SQL or prose.',
      },
      {
        q: 'Is my text sent to a server?',
        a: 'No. The diff algorithm runs completely in your browser.',
      },
    ],
  },
  'text-formatter': {
    path: '/text-formatter',
    name: 'Text Formatter',
    title: 'Text Formatter — Clean, Trim & Format Text Online | DevTools',
    description:
      'Clean and format text online: trim whitespace, fix casing, remove duplicate lines and normalize spacing. Free, instant and 100% in-browser.',
    intro:
      'Fix messy text in one click — collapse repeated spaces, trim line endings, convert case and normalize formatting. Handy for cleaning data exports, log lines or copy-pasted content before using it elsewhere.',
    features: [
      'Trim and collapse whitespace',
      'Case conversion',
      'Line-level cleanup',
      'Instant, offline processing',
    ],
    faqs: [
      {
        q: 'What kinds of cleanup can this tool do?',
        a: 'It normalizes whitespace, trims lines and applies common text transformations so pasted content becomes consistent and clean.',
      },
      {
        q: 'Is there a size limit?',
        a: 'No hard limit — since processing is local, it handles large documents as fast as your browser allows.',
      },
    ],
  },
  'jwt-tool': {
    path: '/jwt-tool',
    name: 'JWT Decoder & Encoder',
    title: 'JWT Decoder — Decode & Inspect JSON Web Tokens Online | DevTools',
    description:
      'Decode JWT tokens online: inspect header, payload and expiry instantly. Encode test tokens too. 100% client-side — tokens never leave your browser.',
    intro:
      'Paste a JSON Web Token to instantly decode its header and payload, check the algorithm, issuer, subject and expiration claims, and debug authentication issues. You can also build test tokens for development.',
    features: [
      'Decode JWT header and payload',
      'Human-readable claim inspection',
      'Encode test tokens',
      'Fully client-side — safe for real tokens',
    ],
    faqs: [
      {
        q: 'Is it safe to paste a real JWT here?',
        a: 'Yes. Decoding happens entirely in your browser — the token is never transmitted. Still, avoid sharing production tokens with anyone.',
      },
      {
        q: 'Why can I read a JWT without the secret?',
        a: 'JWTs are signed, not encrypted. The header and payload are just Base64Url-encoded JSON; the secret is only needed to verify or create the signature.',
      },
      {
        q: 'How do I check if my token is expired?',
        a: 'Decode the token and look at the exp claim in the payload — it is a Unix timestamp. You can convert it with our Epoch Converter.',
      },
    ],
  },
  'base64-tool': {
    path: '/base64-tool',
    name: 'Base64 Encoder & Decoder',
    title: 'Base64 Encode & Decode Online — Free Base64 Converter | DevTools',
    description:
      'Encode text to Base64 or decode Base64 to text online, free and instant. Supports UTF-8. 100% in-browser — no data upload.',
    intro:
      'Convert text to Base64 and back instantly. Useful for debugging API payloads, basic-auth headers, data URIs and configuration values. Full UTF-8 support with instant results.',
    features: [
      'Encode text to Base64',
      'Decode Base64 to readable text',
      'UTF-8 safe',
      'Instant, offline conversion',
    ],
    faqs: [
      {
        q: 'Is Base64 encryption?',
        a: 'No. Base64 is an encoding, not encryption — anyone can decode it. Never use it to protect secrets.',
      },
      {
        q: 'Why does my Base64 string fail to decode?',
        a: 'Common causes are missing padding (=), URL-safe variants (- and _ instead of + and /), or line breaks inside the string.',
      },
      {
        q: 'Is my data private?',
        a: 'Yes. Encoding and decoding run locally in your browser.',
      },
    ],
  },
  'epoch-converter': {
    path: '/epoch-converter',
    name: 'Epoch & Unix Timestamp Converter',
    title: 'Epoch Converter — Unix Timestamp to Date Online | DevTools',
    description:
      'Convert Unix timestamps to human-readable dates and back, in seconds or milliseconds, with UTC, local and ISO 8601 output. Free and instant.',
    intro:
      'Convert Unix epoch timestamps to readable dates and convert dates back to epoch — with automatic detection of seconds vs milliseconds and output in UTC, your local timezone and ISO 8601 format.',
    features: [
      'Epoch → date and date → epoch',
      'Auto-detects seconds vs milliseconds',
      'UTC, local time and ISO 8601 output',
      'One-click current timestamp',
    ],
    faqs: [
      {
        q: 'What is a Unix timestamp?',
        a: 'The number of seconds (or milliseconds) elapsed since January 1, 1970 UTC — the standard machine representation of time in databases, logs and APIs.',
      },
      {
        q: 'How do I know if my timestamp is in seconds or milliseconds?',
        a: '10-digit values are seconds, 13-digit values are milliseconds. This tool detects the unit automatically.',
      },
      {
        q: 'What happens in 2038?',
        a: 'Systems storing timestamps as signed 32-bit integers overflow on January 19, 2038. Modern 64-bit systems and this tool are unaffected.',
      },
    ],
  },
};
